import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Text,
  VStack,
  HStack,
  IconButton,
  Button,
  Input,
  Spinner,
  Switch,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalFooter,
  useDisclosure,
} from '@chakra-ui/react';
import { FiX } from 'react-icons/fi';
import {
  LuPencilLine,
  LuChevronDown,
  LuPalette,
  LuSend,
  LuMapPin,
  LuArrowLeft,
  LuUpload,
  LuLink,
  LuTrash2,
  LuPackage,
  LuShieldCheck,
} from 'react-icons/lu';
import api from '../../../services/api';
import campaignService from '../../../services/campaignService';
import { useAuth } from '../../../contexts/AuthContext';
import { useCampaignComplianceStatus } from '../../../hooks/useCampaignComplianceStatus';
import VariableChipInput from './VariableChipInput';
import ComplianceReviewModal from '../table/ComplianceReviewModal';

// ── Style tokens ──────────────────────────────────────────────────────────────

const PRODUCT_OPTIONS = [
  { key: '4x6_postcard',  label: '4×6 Postcard (US)',        recommended: true },
  { key: '5x7_postcard',  label: '5×7 Postcard (US)' },
  { key: 'a6_postcard',   label: 'A6 Postcard (ISO/UK)' },
  { key: 'a5_postcard',   label: 'A5 Postcard (ISO/UK)' },
  { key: '4x6_card',      label: '4×6 Card (US)' },
  { key: '5x7_card',      label: '5×7 Card (US)' },
  { key: 'a6_card',       label: 'A6 Card (ISO/UK)' },
  { key: 'a5_card',       label: 'A5 Card (ISO/UK)' },
  { key: '7x10_letter',   label: '7×10 Letter (US)' },
  { key: 'a4_letter',     label: 'A4 Letter (ISO/UK)' },
];

// Optimal word budget per card format + orientation (calibrated to the ideal
// handwriting size on each card's writable area, minus margins + the QR block).
// The note isn't hard-blocked past this — the printed font shrinks to fit — but
// it's the point where the counter warns the writing is getting long.
const WORD_BUDGETS = {
  '4x6_postcard': { landscape: 55,  portrait: 55  },
  '5x7_postcard': { landscape: 80,  portrait: 80  },
  'a6_postcard':  { landscape: 55,  portrait: 50  },
  'a5_postcard':  { landscape: 120, portrait: 125 },
  '4x6_card':     { landscape: 55,  portrait: 55  },
  '5x7_card':     { landscape: 80,  portrait: 80  },
  'a6_card':      { landscape: 55,  portrait: 50  },
  'a5_card':      { landscape: 120, portrait: 125 },
  '7x10_letter':  { landscape: 180, portrait: 180 },
  'a4_letter':    { landscape: 265, portrait: 260 },
};

/** Count words in a note template; each {{variable}} chip counts as one word. */
const countNoteWords = (text) => {
  const trimmed = (text || '').trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).length;
};

/** A small color-coded status pill: "• N label" (color is an "r,g,b" string). */
const CountChip = ({ color, count, label }) => (
  <HStack
    spacing={1.5}
    px={2.5}
    py={1}
    borderRadius="full"
    bg={`rgba(${color},0.10)`}
    border={`1px solid rgba(${color},0.25)`}
  >
    <Box w="6px" h="6px" borderRadius="full" bg={`rgba(${color},0.9)`} flexShrink={0} />
    <Text color={`rgba(${color},0.95)`} fontSize="11px" fontWeight="600" whiteSpace="nowrap">
      {count} {label}
    </Text>
  </HStack>
);

const menuListStyles = {
  bg: '#111111',
  border: '1px solid rgba(255,255,255,0.10)',
  borderRadius: '10px',
  py: 1,
  px: 1,
  minW: '200px',
  shadow: '0 8px 24px rgba(0,0,0,0.5)',
  zIndex: 1500,
};

const menuItemStyles = {
  bg: 'transparent',
  color: 'rgba(255,255,255,0.75)',
  fontSize: 'sm',
  borderRadius: '6px',
  mx: 0.5,
  py: 2,
  _hover: { bg: 'rgba(255,255,255,0.05)', color: '#fafafa' },
  _focus: { bg: 'rgba(255,255,255,0.05)' },
};

// Shared styling for the compact text inputs used across the brand + test forms.
const inputFieldStyles = {
  size: 'sm',
  h: '36px',
  bg: '#0a0a0a',
  border: '1px solid rgba(255,255,255,0.10)',
  borderRadius: '8px',
  color: '#fafafa',
  fontSize: 'sm',
  _placeholder: { color: 'rgba(255,255,255,0.22)' },
  _hover: { borderColor: 'rgba(255,255,255,0.2)' },
  _focus: { borderColor: 'rgba(255,255,255,0.35)', boxShadow: 'none', outline: 'none' },
};

const dropdownBtnStyles = {
  variant: 'outline',
  w: '100%',
  justifyContent: 'space-between',
  textAlign: 'left',
  fontWeight: 'normal',
  size: 'sm',
  h: '36px',
  bg: '#0a0a0a',
  border: '1px solid',
  borderColor: 'rgba(255,255,255,0.10)',
  fontSize: 'sm',
  borderRadius: '8px',
  _hover: { borderColor: 'rgba(255,255,255,0.2)' },
  _expanded: { borderColor: 'rgba(255,255,255,0.3)', boxShadow: 'none' },
  _focus: { boxShadow: 'none', outline: 'none' },
  _focusVisible: { boxShadow: 'none', outline: 'none' },
  _disabled: { opacity: 0.4, cursor: 'not-allowed' },
};

// ── ScribelessPanel ───────────────────────────────────────────────────────────

const ScribelessPanel = ({ onClose, campaignId, orientation = 'landscape', template = 'signature', onOrientationChange, onTemplateChange, onBrandPreviewChange, onNotePreviewChange }) => {
  // Panel view: 'main' | 'brand' | 'test'
  const [view, setView] = useState('main');

  const { user } = useAuth();
  const { isApproved: isCampaignApproved, complianceStatus: verdictStatus } = useCampaignComplianceStatus(campaignId);
  const {
    isOpen: isApprovalModalOpen,
    onOpen: onApprovalModalOpen,
    onClose: onApprovalModalClose,
  } = useDisclosure();

  // Brand profile form state
  const [logoBase64, setLogoBase64]         = useState('');
  const [logoUrl, setLogoUrl]               = useState('');
  const [logoInputMode, setLogoInputMode]   = useState('upload'); // 'upload' | 'url'
  const [logoPreview, setLogoPreview]       = useState('');
  const [tagline, setTagline]               = useState('');
  const [qrDestination, setQrDestination]   = useState('');
  const [isSavingBrand, setIsSavingBrand]   = useState(false);
  const [brandSaved, setBrandSaved]         = useState(false);
  const [brandDirty, setBrandDirty]         = useState(false);
  // Snapshot of the last persisted brand profile. Used to revert the form when
  // the user backs out without saving, so the panel always reflects Mongo.
  const savedBrandRef = useRef({
    logoBase64: '', logoUrl: '', logoInputMode: 'upload', tagline: '', qrDestination: '',
  });
  const [isLoadingBrand, setIsLoadingBrand] = useState(true);
  const [isDeletingBrand, setIsDeletingBrand] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const logoInputRef                        = useRef(null);

  // Handwritten note message (typed directly with variable chips)
  const [noteBody, setNoteBody]                 = useState('');
  // The persisted message-draft group for this campaign's single handwritten note.
  // Tracked so re-saving edits the same document instead of creating new ones.
  const [noteGroupId, setNoteGroupId]           = useState(null);
  const [isSavingNote, setIsSavingNote]         = useState(false);
  const [noteSaved, setNoteSaved]               = useState(false);

  const [selectedProduct, setSelectedProduct]   = useState(PRODUCT_OPTIONS[0]); // 4x6 Postcard (US) default

  // Include envelope
  const [includeEnvelope, setIncludeEnvelope]   = useState(false);

  // Include brand materials (logo + tagline) on the card front
  const [includeBrand, setIncludeBrand]         = useState(true);

  // Address summary
  const [addressSummary, setAddressSummary]     = useState(null);
  const [isLoadingAddr, setIsLoadingAddr]       = useState(false);

  // Send state
  const [isSending, setIsSending]               = useState(false);
  const [sendResult, setSendResult]             = useState(null);

  // ── Sample / test card state ────────────────────────────────────────────────
  // A single card mailed to the advisor's own address so they can review the
  // physical print before sending to the whole campaign. It inherits every
  // production setting (format, orientation, brand, envelope); the only inputs
  // are the address + an editable copy of the note (edits here are test-only).
  const [testForm, setTestForm] = useState({
    first_name: '', last_name: '', address1: '', address2: '',
    city: '', state: '', postal_code: '', country: 'US',
  });
  const [testNote, setTestNote]             = useState('');
  const [isSendingTest, setIsSendingTest]   = useState(false);
  const [testResult, setTestResult]         = useState(null);
  const setTF = (key) => (e) => {
    const { value } = e.target;
    setTestForm((f) => ({ ...f, [key]: value }));
  };

  // ── Load saved brand profile ────────────────────────────────────────────────
  // The brand form is an editable view of the persisted profile, so we
  // hydrate it from the backend on mount instead of starting blank.
  useEffect(() => {
    let cancelled = false;
    const fetchProfile = async () => {
      setIsLoadingBrand(true);
      try {
        const { data } = await api.get('/api/scribeless/card-profile');
        if (cancelled) return;
        if (data.logo_url) {
          setLogoUrl(data.logo_url);
          setLogoPreview(data.logo_url);
          setLogoInputMode('url');
        } else if (data.logo_base64) {
          setLogoBase64(data.logo_base64);
          setLogoPreview(data.logo_base64);
          setLogoInputMode('upload');
        }
        setTagline(data.tagline ?? '');
        setQrDestination(data.qr_destination ?? '');
        savedBrandRef.current = {
          logoBase64:    data.logo_url ? '' : (data.logo_base64 ?? ''),
          logoUrl:       data.logo_url ?? '',
          logoInputMode: data.logo_url ? 'url' : 'upload',
          tagline:       data.tagline ?? '',
          qrDestination: data.qr_destination ?? '',
        };
      } catch {
        // 404 → no profile yet; leave the form blank.
      } finally {
        if (!cancelled) setIsLoadingBrand(false);
      }
    };
    fetchProfile();
    return () => { cancelled = true; };
  }, []);

  // ── Fetch address summary ──────────────────────────────────────────────────
  useEffect(() => {
    if (!campaignId) return;
    let cancelled = false;
    const fetchSummary = async () => {
      setIsLoadingAddr(true);
      try {
        const { data } = await api.get(`/api/scribeless/campaigns/${campaignId}/address-summary`);
        if (!cancelled) setAddressSummary(data);
      } catch {
        if (!cancelled) setAddressSummary(null);
      } finally {
        if (!cancelled) setIsLoadingAddr(false);
      }
    };
    fetchSummary();
    return () => { cancelled = true; };
  }, [campaignId]);

  // ── Hydrate the campaign's existing handwritten note (single per campaign) ──
  // Lets the user re-open the panel and keep editing the same persisted draft.
  useEffect(() => {
    if (!campaignId) return;
    let cancelled = false;
    const fetchNote = async () => {
      try {
        const { drafts } = await campaignService.getMessageDrafts(campaignId);
        if (cancelled || !Array.isArray(drafts)) return;
        const note = drafts.find((d) => d.typeOfMessage === 'handwritten_note');
        if (note) {
          setNoteGroupId(note.id);
          setNoteBody(note.bodyTemplate || '');
        }
      } catch {
        // No drafts yet — leave the editor blank.
      }
    };
    fetchNote();
    return () => { cancelled = true; };
  }, [campaignId]);

  // ── Load saved note card settings ───────────────────────────────────────────
  // Hydrates product format, orientation, and template from the per-campaign
  // settings doc on panel mount so reopening the panel restores the last state.
  useEffect(() => {
    if (!campaignId) return;
    let cancelled = false;
    const fetchSettings = async () => {
      try {
        const { data } = await api.get(`/api/scribeless/campaigns/${campaignId}/note-settings`);
        if (cancelled) return;
        if (data.product_key) {
          const match = PRODUCT_OPTIONS.find((p) => p.key === data.product_key);
          if (match) setSelectedProduct(match);
        }
        if (data.orientation) onOrientationChange?.(data.orientation);
        if (data.template) onTemplateChange?.(data.template);
        if (typeof data.include_envelope === 'boolean') setIncludeEnvelope(data.include_envelope);
        if (typeof data.include_brand === 'boolean') setIncludeBrand(data.include_brand);
      } catch {
        // 404 → no settings saved yet; keep defaults.
      }
    };
    fetchSettings();
    return () => { cancelled = true; };
    // Run once on mount — deps intentionally omitted for callbacks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  // ── Auto-save note card settings ─────────────────────────────────────────────
  // Debounced save whenever product, orientation, template, or envelope changes.
  const saveSettingsTimeoutRef = useRef(null);
  useEffect(() => {
    if (!campaignId) return;
    if (saveSettingsTimeoutRef.current) clearTimeout(saveSettingsTimeoutRef.current);
    saveSettingsTimeoutRef.current = setTimeout(() => {
      api.patch(`/api/scribeless/campaigns/${campaignId}/note-settings`, {
        product_key: selectedProduct.key,
        orientation,
        template,
        include_envelope: includeEnvelope,
        include_brand: includeBrand,
      }).catch(() => {});
    }, 800);
    return () => clearTimeout(saveSettingsTimeoutRef.current);
  }, [campaignId, selectedProduct, orientation, template, includeEnvelope, includeBrand]);

  // Whether the advisor has any brand materials worth printing on the card.
  const hasBrandMaterials = !!logoBase64 || !!logoUrl || !!tagline.trim() || !!qrDestination.trim();

  // Address readiness — notes can only be mailed to contacts with a full address.
  const readyCount       = addressSummary?.ready ?? 0;
  const needsUnlockCount = addressSummary?.needs_unlock ?? 0;
  const missingCount     = addressSummary?.missing_address ?? 0;
  const sentCount        = addressSummary?.already_sent ?? 0;
  const hasReadyAddresses = readyCount > 0;

  // ── Word budget for the selected card format + orientation ──
  const noteWordCount = countNoteWords(noteBody);
  const wordBudget =
    (WORD_BUDGETS[selectedProduct.key] || WORD_BUDGETS['4x6_postcard'])[orientation] ||
    WORD_BUDGETS['4x6_postcard'].landscape;
  const overBudget = noteWordCount > wordBudget;
  const nearBudget = !overBudget && noteWordCount >= Math.round(wordBudget * 0.85);
  const counterColor = overBudget
    ? 'rgba(239,68,68,0.9)'
    : nearBudget
      ? 'rgba(251,191,36,0.9)'
      : 'rgba(255,255,255,0.3)';

  // ── Push brand-preview state up so the card can render logo/tagline (back) + QR (front) ──
  useEffect(() => {
    const show = includeBrand && hasBrandMaterials;
    onBrandPreviewChange?.(
      show
        ? {
            logo: logoUrl || logoBase64 || null,
            tagline: tagline.trim() || null,
            qr: qrDestination.trim() || null,
          }
        : null
    );
  }, [includeBrand, hasBrandMaterials, logoBase64, tagline, qrDestination, onBrandPreviewChange]);

  // Clear the preview when the panel unmounts (e.g. switching away from Scribeless).
  useEffect(() => () => onBrandPreviewChange?.(null), [onBrandPreviewChange]);

  // ── Push the typed message body up so it renders on the card front ──
  useEffect(() => {
    onNotePreviewChange?.(noteBody.trim() ? noteBody : null);
  }, [noteBody, onNotePreviewChange]);

  useEffect(() => () => onNotePreviewChange?.(null), [onNotePreviewChange]);

  // ── Save the handwritten note ────────────────────────────────────────────────
  // Persists the note via the same message-draft infra used for email subject+body
  // (contact_field_definitions + values), tagged type_of_message="handwritten_note".
  // First save creates the draft; later saves update the same document.
  const handleSaveNote = useCallback(async () => {
    if (!noteBody.trim() || isSavingNote) return;
    setIsSavingNote(true);
    setNoteSaved(false);
    const payload = {
      email_name:       'Handwritten Note',
      type_of_message:  'handwritten_note',
      message_type:     'note',
      enable_html:      false,
      subject_template: '',
      body_template:    noteBody,
    };
    try {
      if (noteGroupId) {
        await campaignService.updateMessageDraft(campaignId, noteGroupId, payload);
      } else {
        const result = await campaignService.createMessageDraft(campaignId, payload);
        if (result?.message_group_id) setNoteGroupId(result.message_group_id);
      }
      setNoteSaved(true);
      setTimeout(() => setNoteSaved(false), 1800);
    } catch {
      // Keep the editor content so the user can retry.
    } finally {
      setIsSavingNote(false);
    }
  }, [campaignId, noteBody, noteGroupId, isSavingNote]);

  const canSaveNote = !!noteBody.trim() && !isSavingNote;

  // ── Send notes ─────────────────────────────────────────────────────────────
  const handleSend = useCallback(async () => {
    if (!noteBody.trim()) return;
    setIsSending(true);
    setSendResult(null);
    try {
      const { data } = await api.post('/api/scribeless/send-notes', {
        campaign_id:      campaignId,
        body_template:    noteBody,
        product_key:      selectedProduct.key,
        orientation:      orientation,
        template:         template,
        include_envelope: includeEnvelope,
        include_brand:    includeBrand && hasBrandMaterials,
      });
      setSendResult(data);
    } catch (err) {
      setSendResult({ error: err?.response?.data?.detail ?? 'Send failed. Please try again.' });
    } finally {
      setIsSending(false);
    }
  }, [campaignId, noteBody, selectedProduct, orientation, template, includeEnvelope, includeBrand, hasBrandMaterials]);

  const canSend = !!noteBody.trim() && !isSending;

  // ── Open the sample-card view, seeded with the current note ──────────────────
  const openTestView = useCallback(() => {
    setTestNote(noteBody);
    setTestResult(null);
    setTestForm((f) => ({
      ...f,
      first_name: f.first_name || (user?.first_name ?? ''),
      last_name:  f.last_name  || (user?.last_name  ?? ''),
    }));
    setView('test');
  }, [noteBody, user]);

  const canSendTest =
    !isSendingTest &&
    !!testNote.trim() &&
    !!testForm.first_name.trim() &&
    !!testForm.address1.trim() &&
    !!testForm.city.trim() &&
    !!testForm.state.trim() &&
    !!testForm.postal_code.trim() &&
    !!testForm.country.trim();

  // ── Send a single sample card to the advisor's own address ───────────────────
  const handleSendTest = useCallback(async () => {
    if (!canSendTest) return;
    setIsSendingTest(true);
    setTestResult(null);
    try {
      const { data } = await api.post('/api/scribeless/send-test-card', {
        campaign_id:      campaignId,
        first_name:       testForm.first_name.trim(),
        last_name:        testForm.last_name.trim(),
        address: {
          address1:    testForm.address1.trim(),
          address2:    testForm.address2.trim() || undefined,
          city:        testForm.city.trim(),
          state:       testForm.state.trim(),
          postal_code: testForm.postal_code.trim(),
          country:     testForm.country.trim() || 'US',
        },
        body_template:    testNote,
        product_key:      selectedProduct.key,
        orientation:      orientation,
        template:         template,
        include_envelope: includeEnvelope,
        include_brand:    includeBrand && hasBrandMaterials,
      });
      setTestResult(data);
    } catch (err) {
      setTestResult({ error: err?.response?.data?.detail ?? 'Could not send the sample card. Please try again.' });
    } finally {
      setIsSendingTest(false);
    }
  }, [canSendTest, campaignId, testForm, testNote, selectedProduct, orientation, template, includeEnvelope, includeBrand, hasBrandMaterials]);

  // ── Logo file picker ───────────────────────────────────────────────────────
  const handleLogoFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const result = ev.target.result;
      setLogoBase64(result);
      setLogoUrl('');         // new file replaces any previously entered URL
      setLogoPreview(result);
      setLogoInputMode('upload');
      setBrandSaved(false);
      setBrandDirty(true);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  // ── Save brand profile ─────────────────────────────────────────────────────
  const handleSaveBrand = useCallback(async () => {
    setIsSavingBrand(true);
    setBrandSaved(false);
    try {
      await api.patch('/api/scribeless/card-profile', {
        logo_url:       logoInputMode === 'url' ? (logoUrl.trim() || null) : null,
        logo_base64:    logoInputMode === 'upload' ? logoBase64 : null,
        tagline:        tagline.trim(),
        qr_destination: qrDestination.trim(),
      });
      // Sync panel to match what was just persisted — clears the mode that
      // wasn't saved so the form is in a clean, accurate state.
      const savedLogoBase64 = logoInputMode === 'url' ? '' : logoBase64;
      const savedLogoUrl     = logoInputMode === 'url' ? logoUrl.trim() : '';
      if (logoInputMode === 'url') {
        setLogoBase64('');
        setLogoPreview(savedLogoUrl);
      } else {
        setLogoUrl('');
        setLogoPreview(savedLogoBase64);
      }
      savedBrandRef.current = {
        logoBase64:    savedLogoBase64,
        logoUrl:       savedLogoUrl,
        logoInputMode,
        tagline:       tagline.trim(),
        qrDestination: qrDestination.trim(),
      };
      setBrandSaved(true);
      setBrandDirty(false);
      setTimeout(() => setBrandSaved(false), 1800);
    } catch {
      // keep form open on error
    } finally {
      setIsSavingBrand(false);
    }
  }, [logoInputMode, logoBase64, logoUrl, tagline, qrDestination]);

  // ── Delete brand profile ────────────────────────────────────────────────────
  const handleDeleteBrand = useCallback(async () => {
    setIsDeletingBrand(true);
    try {
      await api.delete('/api/scribeless/card-profile');
      setLogoBase64('');
      setLogoUrl('');
      setLogoPreview('');
      setLogoInputMode('upload');
      setTagline('');
      setQrDestination('');
      setBrandSaved(false);
      setBrandDirty(false);
      savedBrandRef.current = {
        logoBase64: '', logoUrl: '', logoInputMode: 'upload', tagline: '', qrDestination: '',
      };
    } catch {
      // 404 → nothing to delete; reset the form anyway.
      setLogoBase64('');
      setLogoUrl('');
      setLogoPreview('');
      setLogoInputMode('upload');
      setTagline('');
      setQrDestination('');
    } finally {
      setIsDeletingBrand(false);
      setShowDeleteConfirm(false);
    }
  }, []);

  // ── Leave the brand view ────────────────────────────────────────────────────
  // Discards any unsaved edits and restores the form to the last persisted
  // profile, so re-opening the panel always shows what's actually in Mongo.
  const handleLeaveBrand = useCallback(() => {
    const snap = savedBrandRef.current;
    setLogoBase64(snap.logoBase64);
    setLogoUrl(snap.logoUrl);
    setLogoInputMode(snap.logoInputMode);
    setLogoPreview(snap.logoUrl || snap.logoBase64);
    setTagline(snap.tagline);
    setQrDestination(snap.qrDestination);
    setBrandDirty(false);
    setBrandSaved(false);
    setView('main');
  }, []);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box h="100%" display="flex" flexDirection="column" bg="#111111" overflow="hidden">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <HStack
        px={4}
        py={3}
        borderBottom="1px solid rgba(255,255,255,0.10)"
        justify="space-between"
        flexShrink={0}
      >
        <HStack spacing={2}>
          {view !== 'main' ? (
            <IconButton
              icon={<LuArrowLeft size={16} />}
              variant="ghost"
              color="rgba(255,255,255,0.5)"
              size="sm"
              aria-label="Back"
              onClick={() => (view === 'brand' ? handleLeaveBrand() : setView('main'))}
              _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.06)' }}
              _focus={{ boxShadow: 'none', outline: 'none' }}
            />
          ) : (
            <Box color="rgba(255,255,255,0.4)">
              <LuPencilLine size={16} />
            </Box>
          )}
          <Text color="#fafafa" fontSize="md" fontWeight="600" letterSpacing="-0.01em" noOfLines={1}>
            {view === 'brand'
              ? 'Brand & Card Setup'
              : view === 'test'
                ? 'Order a Sample Card'
                : 'Handwritten Notes'}
          </Text>
        </HStack>

        <HStack spacing={0}>
          {view === 'main' && (
            <Tooltip
              label="Brand & card setup"
              placement="top"
              hasArrow
              openDelay={300}
              bg="#111111"
              color="#fafafa"
              fontSize="xs"
              borderRadius="6px"
              border="1px solid rgba(255,255,255,0.10)"
              boxShadow="0 4px 12px rgba(0,0,0,0.4)"
            >
              <IconButton
                icon={<LuPalette size={16} />}
                variant="ghost"
                color="rgba(255,255,255,0.4)"
                size="sm"
                aria-label="Brand and card setup"
                onClick={() => setView('brand')}
                _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.06)' }}
                _focus={{ boxShadow: 'none', outline: 'none' }}
                _focusVisible={{ boxShadow: 'none', outline: 'none' }}
              />
            </Tooltip>
          )}
          <IconButton
            icon={<FiX />}
            onClick={onClose}
            variant="ghost"
            color="rgba(255,255,255,0.5)"
            fontSize="18px"
            size="sm"
            aria-label="Close panel"
            transition="all 0.2s ease"
            _hover={{ color: 'rgba(239,68,68,0.8)', bg: 'transparent' }}
            _focus={{ boxShadow: 'none', outline: 'none' }}
            _focusVisible={{ boxShadow: 'none', outline: 'none' }}
          />
        </HStack>
      </HStack>

      {/* ── Brand setup view ───────────────────────────────────────────────── */}
      {view === 'brand' && (
        <Box
          flex={1}
          overflowY="auto"
          px={4}
          py={4}
          sx={{
            '&::-webkit-scrollbar': { width: '4px' },
            '&::-webkit-scrollbar-thumb': { bg: 'rgba(255,255,255,0.08)', borderRadius: '2px' },
          }}
        >
          {isLoadingBrand ? (
            <Box display="flex" alignItems="center" justifyContent="center" py={12}>
              <Spinner size="sm" color="rgba(255,255,255,0.4)" />
            </Box>
          ) : (
          <VStack spacing={4} align="stretch">

            {/* Logo upload */}
            <Box>
              <Text color="rgba(255,255,255,0.65)" fontSize="sm" fontWeight="600" mb={0.5}>
                Company logo
              </Text>
              <Text color="rgba(255,255,255,0.30)" fontSize="xs" mb={2.5} lineHeight="1.5">
                Printed in the header of every card — keep it on a transparent or white background for best results.
              </Text>

              {/* Upload / URL mode tabs */}
              <HStack spacing={0} mb={3} bg="rgba(255,255,255,0.04)" borderRadius="8px" p="3px" w="fit-content">
                {['upload', 'url'].map((mode) => (
                  <Box
                    key={mode}
                    as="button"
                    type="button"
                    px={3}
                    py={1}
                    borderRadius="6px"
                    fontSize="12px"
                    fontWeight="500"
                    cursor="pointer"
                    transition="all 0.15s ease"
                    bg={logoInputMode === mode ? 'rgba(255,255,255,0.10)' : 'transparent'}
                    color={logoInputMode === mode ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.35)'}
                    _hover={{ color: 'rgba(255,255,255,0.7)' }}
                    onClick={() => {
                      setLogoInputMode(mode);
                      // Tab switch alone does NOT change the preview —
                      // the card continues to show whatever was last saved/entered.
                      setBrandSaved(false);
                    }}
                  >
                    {mode === 'upload' ? 'Upload file' : 'Use URL'}
                  </Box>
                ))}
              </HStack>

              <input
                ref={logoInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                style={{ display: 'none' }}
                onChange={handleLogoFile}
              />

              {logoInputMode === 'url' ? (
                /* URL input mode */
                <VStack spacing={2} align="stretch">
                  <Input
                    value={logoUrl}
                    onChange={(e) => {
                      const val = e.target.value;
                      setLogoUrl(val);
                      setLogoPreview(val);
                      if (val) setLogoBase64(''); // new URL replaces any uploaded file
                      setBrandSaved(false);
                      setBrandDirty(true);
                    }}
                    placeholder="https://yoursite.com/logo.png"
                    size="sm"
                    h="36px"
                    bg="#0a0a0a"
                    border="1px solid rgba(255,255,255,0.10)"
                    borderRadius="8px"
                    color="rgba(255,255,255,0.85)"
                    fontSize="13px"
                    _placeholder={{ color: 'rgba(255,255,255,0.22)' }}
                    _hover={{ borderColor: 'rgba(255,255,255,0.20)' }}
                    _focus={{ borderColor: 'rgba(255,255,255,0.35)', boxShadow: 'none', outline: 'none' }}
                  />
                  {/* Show URL logo if entered; fall back to base64 if switching from upload */}
                  {logoUrl ? (
                    <Box
                      borderRadius="10px"
                      border="1px solid rgba(255,255,255,0.10)"
                      bg="rgba(255,255,255,0.03)"
                      display="flex" alignItems="center" justifyContent="center"
                      px={4} py={4} minH="72px"
                    >
                      <img
                        src={logoUrl}
                        alt="Logo preview"
                        style={{ maxHeight: '48px', maxWidth: '100%', objectFit: 'contain' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </Box>
                  ) : logoBase64 ? (
                    <Box
                      borderRadius="10px"
                      border="1px solid rgba(255,255,255,0.10)"
                      bg="rgba(255,255,255,0.03)"
                      overflow="hidden"
                    >
                      <Box
                        display="flex" alignItems="center" justifyContent="center"
                        px={4} py={4} minH="72px"
                      >
                        <img
                          src={logoBase64}
                          alt="Logo preview"
                          style={{ maxHeight: '48px', maxWidth: '100%', objectFit: 'contain', opacity: 0.75 }}
                        />
                      </Box>
                      <Box borderTop="1px solid rgba(255,255,255,0.07)" px={3} py={2}>
                        <Text color="rgba(255,255,255,0.28)" fontSize="xs">
                          Uploaded via file · enter a URL above to replace
                        </Text>
                      </Box>
                    </Box>
                  ) : null}
                </VStack>
              ) : logoBase64 ? (
                /* Upload mode — has a newly uploaded file */
                <Box
                  position="relative"
                  borderRadius="10px"
                  border="1px solid rgba(255,255,255,0.10)"
                  bg="rgba(255,255,255,0.03)"
                  overflow="hidden"
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    px={4}
                    py={4}
                    minH="80px"
                  >
                    <img
                      src={logoBase64}
                      alt="Company logo preview"
                      style={{ maxHeight: '56px', maxWidth: '100%', objectFit: 'contain' }}
                    />
                  </Box>
                  <Box
                    borderTop="1px solid rgba(255,255,255,0.07)"
                    px={3}
                    py={2}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Text color="rgba(255,255,255,0.28)" fontSize="xs">Logo uploaded</Text>
                    <Button
                      size="xs"
                      variant="ghost"
                      leftIcon={<LuUpload size={11} />}
                      onClick={() => logoInputRef.current?.click()}
                      color="rgba(255,255,255,0.45)"
                      fontWeight="500"
                      fontSize="11px"
                      px={2}
                      h="24px"
                      borderRadius="6px"
                      _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.06)' }}
                      _focus={{ boxShadow: 'none', outline: 'none' }}
                    >
                      Replace
                    </Button>
                  </Box>
                </Box>
              ) : logoUrl ? (
                /* Upload mode — no file yet, but a URL logo is active; show it
                   as current preview so user knows the logo is still set */
                <Box
                  position="relative"
                  borderRadius="10px"
                  border="1px solid rgba(255,255,255,0.10)"
                  bg="rgba(255,255,255,0.03)"
                  overflow="hidden"
                >
                  <Box display="flex" alignItems="center" justifyContent="center" px={4} py={4} minH="80px">
                    <img
                      src={logoUrl}
                      alt="Company logo preview"
                      style={{ maxHeight: '56px', maxWidth: '100%', objectFit: 'contain', opacity: 0.75 }}
                    />
                  </Box>
                  <Box
                    borderTop="1px solid rgba(255,255,255,0.07)"
                    px={3} py={2}
                    display="flex" alignItems="center" justifyContent="space-between"
                  >
                    <Text color="rgba(255,255,255,0.28)" fontSize="xs">Logo from URL · upload to replace</Text>
                    <Button
                      size="xs" variant="ghost"
                      leftIcon={<LuUpload size={11} />}
                      onClick={() => logoInputRef.current?.click()}
                      color="rgba(255,255,255,0.45)" fontWeight="500" fontSize="11px"
                      px={2} h="24px" borderRadius="6px"
                      _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.06)' }}
                      _focus={{ boxShadow: 'none', outline: 'none' }}
                    >
                      Replace
                    </Button>
                  </Box>
                </Box>
              ) : (
                /* Empty drop zone */
                <Box
                  as="button"
                  type="button"
                  w="100%"
                  onClick={() => logoInputRef.current?.click()}
                  borderRadius="10px"
                  border="1.5px dashed rgba(255,255,255,0.13)"
                  bg="rgba(255,255,255,0.015)"
                  py={5}
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  gap="8px"
                  cursor="pointer"
                  transition="all 0.15s ease"
                  _hover={{ borderColor: 'rgba(255,255,255,0.28)', bg: 'rgba(255,255,255,0.03)' }}
                  _focus={{ outline: 'none', borderColor: 'rgba(255,255,255,0.28)' }}
                >
                  <Box
                    w="36px"
                    h="36px"
                    borderRadius="8px"
                    bg="rgba(255,255,255,0.05)"
                    border="1px solid rgba(255,255,255,0.10)"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    color="rgba(255,255,255,0.35)"
                  >
                    <LuUpload size={16} />
                  </Box>
                  <VStack spacing={0.5}>
                    <Text color="rgba(255,255,255,0.55)" fontSize="sm" fontWeight="500">
                      Click to upload logo
                    </Text>
                    <Text color="rgba(255,255,255,0.22)" fontSize="xs">
                      PNG, JPG, WebP or SVG · max 5 MB
                    </Text>
                  </VStack>
                </Box>
              )}
            </Box>

            {/* Tagline */}
            <Box>
              <Text color="rgba(255,255,255,0.65)" fontSize="sm" fontWeight="600" mb={0.5}>
                Tagline
              </Text>
              <Text color="rgba(255,255,255,0.30)" fontSize="xs" mb={2.5} lineHeight="1.5">
                Shown below your logo on the card header — leave blank to skip.
              </Text>
              <Input
                value={tagline}
                onChange={(e) => { setTagline(e.target.value); setBrandSaved(false); setBrandDirty(true); }}
                placeholder="e.g. Wealth Advisor · Registered Investment Advisor"
                size="sm"
                h="36px"
                bg="#0a0a0a"
                border="1px solid rgba(255,255,255,0.10)"
                borderRadius="8px"
                color="#fafafa"
                fontSize="sm"
                _placeholder={{ color: 'rgba(255,255,255,0.22)' }}
                _hover={{ borderColor: 'rgba(255,255,255,0.2)' }}
                _focus={{ borderColor: 'rgba(255,255,255,0.35)', boxShadow: 'none', outline: 'none' }}
              />
            </Box>

            {/* QR destination */}
            <Box>
              <Text color="rgba(255,255,255,0.65)" fontSize="sm" fontWeight="600" mb={0.5}>
                Call-to-action link
              </Text>
              <Text color="rgba(255,255,255,0.30)" fontSize="xs" mb={2.5} lineHeight="1.5">
                A scannable QR code is printed on the back of every card. Paste your booking link, website, or a custom landing page.
              </Text>
              <Input
                value={qrDestination}
                onChange={(e) => { setQrDestination(e.target.value); setBrandSaved(false); setBrandDirty(true); }}
                placeholder="https://calendly.com/your-link"
                size="sm"
                h="36px"
                bg="#0a0a0a"
                border="1px solid rgba(255,255,255,0.10)"
                borderRadius="8px"
                color="#fafafa"
                fontSize="sm"
                _placeholder={{ color: 'rgba(255,255,255,0.22)' }}
                _hover={{ borderColor: 'rgba(255,255,255,0.2)' }}
                _focus={{ borderColor: 'rgba(255,255,255,0.35)', boxShadow: 'none', outline: 'none' }}
              />
            </Box>

          </VStack>
          )}
        </Box>
      )}

      {/* ── Unsaved changes hint ───────────────────────────────────────────── */}
      {view === 'brand' && !isLoadingBrand && brandDirty && (
        <HStack
          px={4}
          py={2}
          borderTop="1px solid rgba(255,255,255,0.10)"
          flexShrink={0}
          spacing={1.5}
          bg="rgba(234,179,8,0.06)"
        >
          <Box w="6px" h="6px" borderRadius="full" bg="rgba(234,179,8,0.8)" flexShrink={0} />
          <Text color="rgba(234,179,8,0.85)" fontSize="xs" fontWeight="500">
            Unsaved changes — save to keep this update.
          </Text>
        </HStack>
      )}

      {/* ── Brand setup footer ─────────────────────────────────────────────── */}
      {view === 'brand' && !isLoadingBrand && (
        <HStack
          px={4}
          py={3}
          borderTop={brandDirty ? 'none' : '1px solid rgba(255,255,255,0.10)'}
          flexShrink={0}
          justify="space-between"
          spacing={2}
        >
          <Button
            leftIcon={isDeletingBrand ? <Spinner size="xs" /> : <LuTrash2 size={13} />}
            onClick={() => setShowDeleteConfirm(true)}
            isDisabled={isSavingBrand || isDeletingBrand}
            bg="transparent"
            color="rgba(239,68,68,0.85)"
            border="1px solid rgba(239,68,68,0.30)"
            borderRadius="full"
            fontWeight="600"
            fontSize="13px"
            h="36px"
            px={4}
            _hover={{ bg: 'rgba(239,68,68,0.10)', borderColor: 'rgba(239,68,68,0.5)' }}
            _disabled={{ opacity: 0.4, cursor: 'not-allowed' }}
            _focus={{ boxShadow: 'none', outline: 'none' }}
          >
            Delete
          </Button>
          <Button
            leftIcon={isSavingBrand ? <Spinner size="xs" /> : <LuLink size={13} />}
            onClick={handleSaveBrand}
            isDisabled={isSavingBrand || isDeletingBrand}
            bg={brandSaved ? 'rgba(34,197,94,0.15)' : '#fafafa'}
            color={brandSaved ? 'rgba(34,197,94,0.9)' : '#0a0a0a'}
            border={brandSaved ? '1px solid rgba(34,197,94,0.3)' : 'none'}
            borderRadius="full"
            fontWeight="600"
            fontSize="13px"
            h="36px"
            px={4}
            _hover={{ opacity: 0.88 }}
            _disabled={{ opacity: 0.4, cursor: 'not-allowed' }}
            _focus={{ boxShadow: 'none', outline: 'none' }}
          >
            {brandSaved ? 'Saved!' : isSavingBrand ? 'Saving\u2026' : 'Save'}
          </Button>
        </HStack>
      )}

      {/* ── Delete confirmation modal ──────────────────────────────────────── */}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => !isDeletingBrand && setShowDeleteConfirm(false)}
        isCentered
        motionPreset="scale"
      >
        <ModalOverlay bg="rgba(0,0,0,0.6)" backdropFilter="blur(2px)" />
        <ModalContent
          bg="#161616"
          border="1px solid rgba(255,255,255,0.10)"
          borderRadius="14px"
          mx={4}
          maxW="380px"
          boxShadow="0 16px 48px rgba(0,0,0,0.6)"
        >
          <ModalBody pt={5} pb={4} px={5}>
            <Text color="#fafafa" fontSize="md" fontWeight="600" mb={1}>
              Delete brand profile?
            </Text>
            <Text color="rgba(255,255,255,0.45)" fontSize="sm" lineHeight="1.55">
              This permanently removes your logo, tagline, and call-to-action link.
              This action cannot be undone.
            </Text>
          </ModalBody>
          <ModalFooter px={5} pb={5} pt={0} gap={2}>
            <Button
              onClick={() => setShowDeleteConfirm(false)}
              isDisabled={isDeletingBrand}
              variant="ghost"
              color="rgba(255,255,255,0.6)"
              fontWeight="600"
              fontSize="13px"
              h="36px"
              borderRadius="full"
              _hover={{ bg: 'rgba(255,255,255,0.06)', color: '#fafafa' }}
              _focus={{ boxShadow: 'none', outline: 'none' }}
            >
              Cancel
            </Button>
            <Button
              leftIcon={isDeletingBrand ? <Spinner size="xs" /> : <LuTrash2 size={13} />}
              onClick={handleDeleteBrand}
              isDisabled={isDeletingBrand}
              bg="rgba(239,68,68,0.9)"
              color="#fff"
              fontWeight="600"
              fontSize="13px"
              h="36px"
              px={4}
              borderRadius="full"
              _hover={{ bg: 'rgba(239,68,68,1)' }}
              _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
              _focus={{ boxShadow: 'none', outline: 'none' }}
            >
              {isDeletingBrand ? 'Deleting\u2026' : 'Delete'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ── Sample / test card view ────────────────────────────────────────── */}
      {view === 'test' && (<>
        <Box
          flex={1}
          overflowY="auto"
          px={4}
          py={4}
          sx={{
            '&::-webkit-scrollbar': { width: '4px' },
            '&::-webkit-scrollbar-thumb': { bg: 'rgba(255,255,255,0.08)', borderRadius: '2px' },
          }}
        >
          <VStack spacing={4} align="stretch">

            {/* Intro */}
            <Text color="rgba(255,255,255,0.35)" fontSize="xs" lineHeight="1.6">
              Mail one card to yourself to check the print before sending to the whole
              campaign. It uses your exact setup below — you only add the address.
            </Text>

            {/* Inherited setup summary */}
            <Box>
              <Text color="rgba(255,255,255,0.45)" fontSize="sm" fontWeight="500" mb={2}>
                Using your current setup
              </Text>
              <HStack spacing={1.5} flexWrap="wrap">
                {[
                  selectedProduct.label,
                  orientation.charAt(0).toUpperCase() + orientation.slice(1),
                  (includeBrand && hasBrandMaterials) ? 'Brand on' : 'Brand off',
                  includeEnvelope ? 'Envelope on' : 'Envelope off',
                ].map((chip) => (
                  <Box
                    key={chip}
                    px={2.5}
                    py={1}
                    borderRadius="full"
                    bg="rgba(255,255,255,0.04)"
                    border="1px solid rgba(255,255,255,0.10)"
                  >
                    <Text color="rgba(255,255,255,0.6)" fontSize="11px" fontWeight="500">
                      {chip}
                    </Text>
                  </Box>
                ))}
              </HStack>
            </Box>

            {/* Recipient (you) */}
            <Box>
              <Text color="rgba(255,255,255,0.45)" fontSize="sm" fontWeight="500" mb={0.5}>
                Mail it to
              </Text>
              <Text color="rgba(255,255,255,0.30)" fontSize="xs" mb={2.5} lineHeight="1.5">
                Your own address — this card won't be sent to any contact.
              </Text>
              <VStack spacing={2} align="stretch">
                <HStack spacing={2}>
                  <Input
                    value={testForm.first_name}
                    onChange={setTF('first_name')}
                    placeholder="First name"
                    {...inputFieldStyles}
                  />
                  <Input
                    value={testForm.last_name}
                    onChange={setTF('last_name')}
                    placeholder="Last name"
                    {...inputFieldStyles}
                  />
                </HStack>
                <Input
                  value={testForm.address1}
                  onChange={setTF('address1')}
                  placeholder="Address line 1"
                  {...inputFieldStyles}
                />
                <Input
                  value={testForm.address2}
                  onChange={setTF('address2')}
                  placeholder="Address line 2 (optional)"
                  {...inputFieldStyles}
                />
                <HStack spacing={2}>
                  <Input
                    value={testForm.city}
                    onChange={setTF('city')}
                    placeholder="City"
                    {...inputFieldStyles}
                  />
                  <Input
                    value={testForm.state}
                    onChange={setTF('state')}
                    placeholder="State"
                    {...inputFieldStyles}
                    w="90px"
                  />
                </HStack>
                <HStack spacing={2}>
                  <Input
                    value={testForm.postal_code}
                    onChange={setTF('postal_code')}
                    placeholder="Postal code"
                    {...inputFieldStyles}
                  />
                  <Input
                    value={testForm.country}
                    onChange={setTF('country')}
                    placeholder="Country"
                    {...inputFieldStyles}
                    w="90px"
                  />
                </HStack>
              </VStack>
            </Box>

            {/* Message (editable, test-only) */}
            <Box>
              <Text color="rgba(255,255,255,0.45)" fontSize="sm" fontWeight="500" mb={0.5}>
                Message
              </Text>
              <Text color="rgba(255,255,255,0.30)" fontSize="xs" mb={2} lineHeight="1.5">
                Pre-filled from your note and the first contact. Edits here only affect this sample.
              </Text>
              <VariableChipInput
                value={testNote}
                onChange={setTestNote}
                placeholder="Write the message for this sample card…"
                isTextarea
                minH="110px"
              />
            </Box>

          </VStack>
        </Box>

        {/* Test send footer */}
        <Box px={4} py={3} borderTop="1px solid rgba(255,255,255,0.10)" flexShrink={0}>
          <VStack spacing={3} align="stretch">
            {testResult && !testResult.error && (
              <Box
                px={3}
                py={2.5}
                borderRadius="8px"
                bg="rgba(34,197,94,0.07)"
                border="1px solid rgba(34,197,94,0.2)"
              >
                <Text color="rgba(34,197,94,0.9)" fontSize="xs" fontWeight="600" mb={testResult.preview_url ? 2 : 0}>
                  Sample card on its way to your address
                </Text>
                {testResult.preview_url && (
                  <img
                    src={testResult.preview_url}
                    alt="Sample card preview"
                    style={{ width: '100%', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.08)' }}
                  />
                )}
              </Box>
            )}
            {testResult?.error && (
              <Box
                px={3}
                py={2}
                borderRadius="8px"
                bg="rgba(239,68,68,0.07)"
                border="1px solid rgba(239,68,68,0.2)"
              >
                <Text color="rgba(239,68,68,0.85)" fontSize="xs">{testResult.error}</Text>
              </Box>
            )}
            <Button
              leftIcon={isSendingTest ? <Spinner size="xs" /> : <LuSend size={13} />}
              isDisabled={!canSendTest}
              onClick={handleSendTest}
              w="100%"
              bg="#fafafa"
              color="#0a0a0a"
              borderRadius="full"
              fontWeight="600"
              fontSize="13px"
              h="36px"
              _hover={{ opacity: 0.88 }}
              _disabled={{ opacity: 0.3, cursor: 'not-allowed' }}
              _focus={{ boxShadow: 'none', outline: 'none' }}
            >
              {isSendingTest
                ? 'Sending\u2026'
                : (testResult && !testResult.error)
                  ? 'Send another sample card'
                  : 'Send sample card to me'}
            </Button>
          </VStack>
        </Box>
      </>)}

      {/* ── Main options view ───────────────────────────────────────────────── */}
      {view === 'main' && (<>

      {/* ── Approval gate banner ───────────────────────────────────────────── */}
      {!isCampaignApproved && (
        <Box
          px={4}
          py={3}
          borderBottom="1px solid"
          borderColor={verdictStatus === 'rejected' ? 'rgba(239,68,68,0.20)' : 'rgba(255,255,255,0.08)'}
          bg={verdictStatus === 'rejected' ? 'rgba(239,68,68,0.05)' : 'rgba(255,255,255,0.02)'}
          flexShrink={0}
        >
          <HStack spacing={2} align="flex-start">
            <Box
              px={2}
              py="2px"
              borderRadius="full"
              bg={verdictStatus === 'rejected' ? 'rgba(239,68,68,0.12)' : 'rgba(251,191,36,0.10)'}
              border={`1px solid ${verdictStatus === 'rejected' ? 'rgba(239,68,68,0.30)' : 'rgba(251,191,36,0.28)'}`}
              flexShrink={0}
              mt="2px"
            >
              <Text
                fontSize="9px"
                fontWeight="700"
                color={verdictStatus === 'rejected' ? 'rgba(248,113,113,0.9)' : 'rgba(251,191,36,0.9)'}
                letterSpacing="0.06em"
                textTransform="uppercase"
              >
                {verdictStatus === 'rejected' ? 'Rejected' : 'Pending Review'}
              </Text>
            </Box>
            <VStack spacing={1.5} align="flex-start" flex={1} minW={0}>
              <Text fontSize="xs" color="rgba(255,255,255,0.45)" lineHeight="1.5">
                {verdictStatus === 'rejected'
                  ? 'Your reviewer rejected this campaign. Revise and request re-review before sending notes.'
                  : 'Sending is locked until a reviewer approves this campaign.'}
              </Text>
              <Button
                leftIcon={<LuShieldCheck size={12} />}
                size="xs"
                h="26px"
                px={3}
                borderRadius="full"
                fontWeight="600"
                fontSize="11px"
                bg="transparent"
                color={verdictStatus === 'rejected' ? 'rgba(248,113,113,0.85)' : 'rgba(251,191,36,0.85)'}
                border={`1px solid ${verdictStatus === 'rejected' ? 'rgba(239,68,68,0.30)' : 'rgba(251,191,36,0.30)'}`}
                onClick={onApprovalModalOpen}
                _hover={{
                  bg: verdictStatus === 'rejected' ? 'rgba(239,68,68,0.08)' : 'rgba(251,191,36,0.08)',
                  borderColor: verdictStatus === 'rejected' ? 'rgba(239,68,68,0.50)' : 'rgba(251,191,36,0.50)',
                  color: verdictStatus === 'rejected' ? 'rgba(248,113,113,1)' : 'rgba(251,191,36,1)',
                }}
                _focus={{ boxShadow: 'none', outline: 'none' }}
              >
                {verdictStatus === 'rejected' ? 'Request Re-review' : 'Send for Approval'}
              </Button>
            </VStack>
          </HStack>
        </Box>
      )}

      {/* ── Scrollable options + address area ──────────────────────────────── */}
      <Box
        flex={1}
        overflowY="auto"
        px={4}
        py={4}
        sx={{
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-thumb': { bg: 'rgba(255,255,255,0.08)', borderRadius: '2px' },
        }}
      >
        <VStack spacing={4} align="stretch">

          {/* 1 — Product */}
          <Box>
            <Text color="rgba(255,255,255,0.45)" fontSize="sm" fontWeight="500" mb={2}>
              Card format
            </Text>
            <Menu placement="bottom-start" matchWidth>
              <MenuButton
                as={Button}
                rightIcon={<LuChevronDown size={14} />}
                color="#fafafa"
                {...dropdownBtnStyles}
              >
                <HStack spacing={2} overflow="hidden">
                  <Text fontSize="sm" noOfLines={1} flex={1} minW={0}>{selectedProduct.label}</Text>
                  {selectedProduct.recommended && (
                    <Box
                      flexShrink={0}
                      px={1.5}
                      py={0.5}
                      borderRadius="4px"
                      bg="rgba(157,113,253,0.15)"
                      border="1px solid rgba(157,113,253,0.3)"
                    >
                      <Text fontSize="9px" fontWeight="600" color="rgba(157,113,253,0.9)" letterSpacing="0.06em" textTransform="uppercase">
                        Recommended
                      </Text>
                    </Box>
                  )}
                </HStack>
              </MenuButton>
              <MenuList {...menuListStyles}>
                {PRODUCT_OPTIONS.map((opt) => (
                  <MenuItem
                    key={opt.key}
                    onClick={() => setSelectedProduct(opt)}
                    {...menuItemStyles}
                    color={selectedProduct.key === opt.key ? '#fafafa' : 'rgba(255,255,255,0.75)'}
                  >
                    <HStack w="100%" justify="space-between">
                      <HStack spacing={2} overflow="hidden" flex={1} minW={0}>
                        <Text fontSize="sm" noOfLines={1}>{opt.label}</Text>
                        {opt.recommended && (
                          <Box
                            flexShrink={0}
                            px={1.5}
                            py={0.5}
                            borderRadius="4px"
                            bg="rgba(157,113,253,0.15)"
                            border="1px solid rgba(157,113,253,0.3)"
                          >
                            <Text fontSize="9px" fontWeight="600" color="rgba(157,113,253,0.9)" letterSpacing="0.06em" textTransform="uppercase">
                              Rec
                            </Text>
                          </Box>
                        )}
                      </HStack>
                      {selectedProduct.key === opt.key && (
                        <Text fontSize="12px" color="rgba(255,255,255,0.85)" fontWeight="600" flexShrink={0}>✓</Text>
                      )}
                    </HStack>
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
          </Box>

          {/* 2 — Orientation */}
          <Box>
            <Text color="rgba(255,255,255,0.45)" fontSize="sm" fontWeight="500" mb={2}>
              Orientation
            </Text>
            <HStack
              spacing={0}
              bg="rgba(255,255,255,0.03)"
              borderRadius="8px"
              p={0.5}
              border="1px solid rgba(255,255,255,0.10)"
            >
              {['landscape', 'portrait'].map((opt) => {
                const isActive = orientation === opt;
                return (
                  <Button
                    key={opt}
                    size="sm"
                    variant="ghost"
                    flex={1}
                    color={isActive ? '#fafafa' : 'rgba(255,255,255,0.4)'}
                    bg={isActive ? 'rgba(255,255,255,0.10)' : 'transparent'}
                    borderRadius="6px"
                    fontWeight={isActive ? '500' : 'normal'}
                    fontSize="sm"
                    textTransform="capitalize"
                    onClick={() => onOrientationChange?.(opt)}
                    _hover={{ bg: isActive ? 'rgba(255,255,255,0.13)' : 'rgba(255,255,255,0.05)', color: '#fafafa' }}
                    _focus={{ boxShadow: 'none', outline: 'none' }}
                    _focusVisible={{ boxShadow: 'none', outline: 'none' }}
                  >
                    {opt}
                  </Button>
                );
              })}
            </HStack>
          </Box>

          {/* 3 — Include brand materials */}
          <HStack justify="space-between" align="center">
            <VStack spacing={0} align="flex-start" flex={1} minW={0} pr={3}>
              <Text color="rgba(255,255,255,0.45)" fontSize="sm" fontWeight="500">
                Include brand materials
              </Text>
              <Text color="rgba(255,255,255,0.25)" fontSize="xs" lineHeight="1.5">
                {hasBrandMaterials
                  ? 'Prints your logo and tagline at the top of the card'
                  : 'Add a logo or tagline in Brand & Card Setup first'}
              </Text>
            </VStack>
            <Switch
              isChecked={includeBrand && hasBrandMaterials}
              isDisabled={!hasBrandMaterials}
              onChange={(e) => setIncludeBrand(e.target.checked)}
              size="sm"
              colorScheme="whiteAlpha"
              sx={{
                'span.chakra-switch__track': {
                  bg: (includeBrand && hasBrandMaterials) ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.12)',
                },
              }}
            />
          </HStack>

          {/* 4 — Note message */}
          <Box>
            <HStack justify="space-between" align="center" mb={2}>
              <Text color="rgba(255,255,255,0.45)" fontSize="sm" fontWeight="500">
                Note message
              </Text>
              <Button
                onClick={handleSaveNote}
                isDisabled={!canSaveNote}
                leftIcon={isSavingNote ? <Spinner size="xs" /> : undefined}
                size="xs"
                h="26px"
                px={3}
                borderRadius="full"
                fontWeight="600"
                fontSize="12px"
                bg={noteSaved ? 'rgba(34,197,94,0.15)' : '#fafafa'}
                color={noteSaved ? 'rgba(34,197,94,0.9)' : '#0a0a0a'}
                border={noteSaved ? '1px solid rgba(34,197,94,0.3)' : 'none'}
                _hover={{ opacity: 0.88 }}
                _disabled={{ opacity: 0.35, cursor: 'not-allowed' }}
                _focus={{ boxShadow: 'none', outline: 'none' }}
              >
                {noteSaved ? 'Saved!' : isSavingNote ? 'Saving\u2026' : 'Save'}
              </Button>
            </HStack>
            <VariableChipInput
              value={noteBody}
              onChange={(v) => { setNoteBody(v); setNoteSaved(false); }}
              placeholder="Write your handwritten note… type {{...}} for custom fields"
              isTextarea
              minH="120px"
            />
            <HStack justify="space-between" align="center" mt={1.5} spacing={2}>
              <Text color="rgba(255,255,255,0.28)" fontSize="11px" lineHeight="1.4" flex={1} minW={0}>
                {overBudget
                  ? 'Over the ideal length — the handwriting will shrink to fit'
                  : 'Optimal length for this card'}
              </Text>
              <Text color={counterColor} fontSize="11px" fontWeight="600" flexShrink={0} fontVariantNumeric="tabular-nums">
                {noteWordCount} / {wordBudget} words
              </Text>
            </HStack>
          </Box>

          {/* 5 — Include envelope */}
          <HStack justify="space-between" align="center">
            <VStack spacing={0} align="flex-start">
              <Text color="rgba(255,255,255,0.45)" fontSize="sm" fontWeight="500">
                Include envelope
              </Text>
              <Text color="rgba(255,255,255,0.25)" fontSize="xs">
                Where supported by product
              </Text>
            </VStack>
            <Switch
              isChecked={includeEnvelope}
              onChange={(e) => setIncludeEnvelope(e.target.checked)}
              size="sm"
              colorScheme="whiteAlpha"
              sx={{
                'span.chakra-switch__track': {
                  bg: includeEnvelope ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.12)',
                },
              }}
            />
          </HStack>

          {/* Address readiness */}
          {isLoadingAddr ? (
            <HStack h="28px" spacing={2}>
              <Spinner size="xs" color="rgba(255,255,255,0.2)" />
              <Text color="rgba(255,255,255,0.25)" fontSize="xs">Checking addresses…</Text>
            </HStack>
          ) : hasReadyAddresses ? (
            <Box
              px={3}
              py={2.5}
              borderRadius="8px"
              bg="rgba(255,255,255,0.03)"
              border="1px solid rgba(255,255,255,0.08)"
            >
              <HStack justify="space-between" align="center" mb={needsUnlockCount > 0 || missingCount > 0 || sentCount > 0 ? 2 : 0}>
                <HStack spacing={2}>
                  <Box w="6px" h="6px" borderRadius="full" bg="rgba(74,222,128,0.85)" flexShrink={0} />
                  <Text color="rgba(255,255,255,0.75)" fontSize="xs" fontWeight="500">
                    {readyCount} of {addressSummary?.total ?? readyCount} addresses ready
                  </Text>
                </HStack>
                <Text color="rgba(74,222,128,0.7)" fontSize="11px" fontWeight="500">
                  ready to mail
                </Text>
              </HStack>
              {(needsUnlockCount > 0 || missingCount > 0 || sentCount > 0) && (
                <HStack spacing={3} flexWrap="wrap">
                  {needsUnlockCount > 0 && (
                    <Text color="rgba(255,255,255,0.3)" fontSize="11px">
                      <Text as="span" color="rgba(99,160,255,0.85)" fontWeight="600">{needsUnlockCount}</Text>
                      {' '}need unlock
                    </Text>
                  )}
                  {missingCount > 0 && (
                    <Text color="rgba(255,255,255,0.3)" fontSize="11px">
                      <Text as="span" color="rgba(251,191,36,0.8)" fontWeight="600">{missingCount}</Text>
                      {' '}no address
                    </Text>
                  )}
                  {sentCount > 0 && (
                    <Text color="rgba(255,255,255,0.3)" fontSize="11px">
                      <Text as="span" color="rgba(148,163,184,0.8)" fontWeight="600">{sentCount}</Text>
                      {' '}already sent
                    </Text>
                  )}
                </HStack>
              )}
            </Box>
          ) : (
            <Box
              px={3}
              py={2.5}
              borderRadius="8px"
              bg="rgba(255,255,255,0.03)"
              border="1px solid rgba(255,255,255,0.08)"
            >
              <HStack justify="space-between" align="center" mb={needsUnlockCount > 0 ? 1.5 : 0}>
                <HStack spacing={2}>
                  <Box w="6px" h="6px" borderRadius="full" bg="rgba(251,191,36,0.7)" flexShrink={0} />
                  <Text color="rgba(255,255,255,0.55)" fontSize="xs" fontWeight="500">
                    {needsUnlockCount > 0 ? 'Addresses need unlocking' : 'No addresses found'}
                  </Text>
                </HStack>
              </HStack>
              {needsUnlockCount > 0 && (
                <Text color="rgba(255,255,255,0.3)" fontSize="11px" lineHeight="1.6" pl="14px">
                  <Text as="span" color="rgba(251,191,36,0.75)" fontWeight="600">{needsUnlockCount}</Text>
                  {` contact${needsUnlockCount !== 1 ? 's have' : ' has'} a locked address — use Fetch address on the contacts table to reveal them.`}
                </Text>
              )}
            </Box>
          )}

        </VStack>
      </Box>

      {/* ── Send result feedback (above footer, outside scroll) ────────────── */}
      {sendResult && !sendResult.error && (
        <Box px={4} pb={2}>
          <Box
            px={3}
            py={2}
            borderRadius="8px"
            bg="rgba(34,197,94,0.07)"
            border="1px solid rgba(34,197,94,0.2)"
          >
            <Text color="rgba(34,197,94,0.9)" fontSize="xs" fontWeight="500">
              {sendResult.sent} note{sendResult.sent !== 1 ? 's' : ''} sent
              {sendResult.skipped > 0 ? `, ${sendResult.skipped} skipped` : ''}
            </Text>
          </Box>
        </Box>
      )}
      {sendResult?.error && (
        <Box px={4} pb={2}>
          <Box
            px={3}
            py={2}
            borderRadius="8px"
            bg="rgba(239,68,68,0.07)"
            border="1px solid rgba(239,68,68,0.2)"
          >
            <Text color="rgba(239,68,68,0.85)" fontSize="xs">
              {sendResult.error}
            </Text>
          </Box>
        </Box>
      )}

      {/* ── Footer — Sample + Send (or approval) buttons ───────────────────── */}
      <HStack
        px={4}
        py={3}
        borderTop="1px solid rgba(255,255,255,0.10)"
        flexShrink={0}
        spacing={2}
      >
        <Tooltip
          label="Order a sample card to yourself first"
          placement="top"
          hasArrow
          openDelay={300}
          bg="#111111"
          color="#fafafa"
          fontSize="xs"
          borderRadius="6px"
          border="1px solid rgba(255,255,255,0.10)"
          boxShadow="0 4px 12px rgba(0,0,0,0.4)"
        >
          <Button
            leftIcon={<LuPackage size={13} />}
            variant="outline"
            onClick={openTestView}
            flexShrink={0}
            bg="transparent"
            color="rgba(255,255,255,0.6)"
            border="1px solid rgba(255,255,255,0.14)"
            fontWeight="500"
            fontSize="13px"
            h="36px"
            px={4}
            borderRadius="full"
            _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.28)' }}
            _focus={{ boxShadow: 'none', outline: 'none' }}
            _focusVisible={{ boxShadow: 'none', outline: 'none' }}
          >
            Sample
          </Button>
        </Tooltip>

        {!isCampaignApproved ? (
          <Button
            leftIcon={<LuShieldCheck size={13} />}
            onClick={onApprovalModalOpen}
            flex={1}
            bg="transparent"
            color="rgba(255,255,255,0.75)"
            border="1px solid rgba(255,255,255,0.18)"
            borderRadius="full"
            fontWeight="600"
            fontSize="13px"
            h="36px"
            _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.35)' }}
            _focus={{ boxShadow: 'none', outline: 'none' }}
            _focusVisible={{ boxShadow: 'none', outline: 'none' }}
          >
            {verdictStatus === 'rejected' ? 'Request Re-review' : 'Send for Approval'}
          </Button>
        ) : (
          <Tooltip
            isDisabled={canSend && hasReadyAddresses}
            label={
              !noteBody.trim()
                ? 'Write a note message first'
                : !hasReadyAddresses
                  ? 'No contacts have a mailing address yet'
                  : ''
            }
            placement="top"
            hasArrow
            openDelay={200}
            bg="#111111"
            color="#fafafa"
            fontSize="xs"
            borderRadius="6px"
            border="1px solid rgba(255,255,255,0.10)"
            boxShadow="0 4px 12px rgba(0,0,0,0.4)"
          >
            <Button
              leftIcon={isSending ? <Spinner size="xs" /> : <LuSend size={13} />}
              isDisabled={!canSend || !hasReadyAddresses}
              onClick={handleSend}
              flex={1}
              bg="#fafafa"
              color="#0a0a0a"
              borderRadius="full"
              fontWeight="600"
              fontSize="13px"
              h="36px"
              _hover={{ opacity: 0.88 }}
              _disabled={{ opacity: 0.3, cursor: 'not-allowed' }}
              _focus={{ boxShadow: 'none', outline: 'none' }}
              _focusVisible={{ boxShadow: 'none', outline: 'none' }}
            >
              {isSending ? 'Sending\u2026' : 'Send Handwritten Notes'}
            </Button>
          </Tooltip>
        )}
      </HStack>

      <ComplianceReviewModal
        isOpen={isApprovalModalOpen}
        onClose={onApprovalModalClose}
        campaignId={campaignId}
      />

      </>)}

    </Box>
  );
};

export default ScribelessPanel;
